/* eslint-disable unicorn/no-null */
/* eslint-disable @typescript-eslint/no-magic-numbers */
import React, { useEffect, useRef, useState } from 'react';
import { FaExpandAlt } from 'react-icons/fa';
import Keyboard from 'react-simple-keyboard';

import 'react-simple-keyboard/build/css/index.css';
import { languageNames, layouts } from './layouts';
import './VirtualKeyboard.css';
import { useVirtualKeyboard } from './VirtualKeyboardContext';

interface Position {
  x: number;
  y: number;
}

interface Size {
  width: number;
  height: number;
}

interface KeyboardInstance {
  setInput: (value: string) => void;
  setOptions: (options: { layout: { default: string[]; shift?: string[] } }) => void;
}

function getMaxLength(input: HTMLInputElement | HTMLTextAreaElement): number | undefined {
  const max = input.getAttribute('maxlength');
  if (max == null || max === '') return undefined;
  const n = Number.parseInt(max, 10);
  return Number.isNaN(n) || n < 0 ? undefined : n;
}

function VirtualKeyboard() {
  const {
    isKeyboardVisible,
    hideKeyboard,
    activeInput,
    currentLanguage,
    setCurrentLanguage,
    isNumericInput,
    lockKeyboard,
    unlockKeyboard,
  } = useVirtualKeyboard();

  const keyboardReference = useRef<KeyboardInstance | null>(null);
  const containerReference = useRef<HTMLDivElement>(null);
  const languageButtonReference = useRef<HTMLButtonElement>(null);
  const languageDropdownReference = useRef<HTMLDivElement>(null);
  const [layoutName, setLayoutName] = useState('default');
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'above' | 'below'>('above');

  const constrainToViewport = (pos: Position, sz: Size): { position: Position; size: Size } => {
    const minWidth = 400;
    const minHeight = 200;
    const padding = 10;

    const maxWidth = window.innerWidth * 0.7;
    const maxHeight = window.innerHeight * 0.7;
    let constrainedWidth = Math.max(minWidth, Math.min(sz.width, maxWidth));
    let constrainedHeight = Math.max(minHeight, Math.min(sz.height, maxHeight));

    let constrainedX = Math.max(
      padding,
      Math.min(pos.x, window.innerWidth - constrainedWidth - padding),
    );
    let constrainedY = Math.max(padding, Math.min(pos.y, window.innerHeight - constrainedHeight));

    if (constrainedX + constrainedWidth > window.innerWidth - padding) {
      constrainedWidth = window.innerWidth - constrainedX - padding;
      constrainedWidth = Math.max(minWidth, constrainedWidth);
    }
    if (constrainedY + constrainedHeight > window.innerHeight - padding) {
      constrainedHeight = window.innerHeight - constrainedY - padding;
      constrainedHeight = Math.max(minHeight, constrainedHeight);
    }

    constrainedX = Math.max(
      padding,
      Math.min(constrainedX, window.innerWidth - constrainedWidth - padding),
    );
    constrainedY = Math.max(
      padding,
      Math.min(constrainedY, window.innerHeight - constrainedHeight - padding),
    );

    return {
      position: { x: constrainedX, y: constrainedY },
      size: { width: constrainedWidth, height: constrainedHeight },
    };
  };

  const loadSavedState = (): { position: Position; size: Size } => {
    const saved = localStorage.getItem('virtual-keyboard-state');
    if (saved) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const parsed = JSON.parse(saved);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (parsed.position && parsed.size > 0 && parsed.size.width > 0 && parsed.size.height > 0) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
          return constrainToViewport(parsed.position, parsed.size);
        }
      } catch {
        // If parsing fails, use defaults
      }
    }
    const padding = 10;
    const defaultWidth = Math.min(800, window.innerWidth * 0.6);
    const defaultHeight = 280;
    const defaultX = Math.max(padding, (window.innerWidth - defaultWidth) / 2);
    const defaultY = Math.max(padding, window.innerHeight);

    return {
      position: { x: defaultX, y: defaultY },
      size: { width: defaultWidth, height: defaultHeight },
    };
  };

  const [position, setPosition] = useState<Position>(loadSavedState().position);
  const [size, setSize] = useState<Size>(loadSavedState().size);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<string>('');
  const dragStartReference = useRef<{ x: number; y: number } | null>(null);
  const resizeStartReference = useRef<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const positionReference = useRef<Position>(loadSavedState().position);
  const sizeReference = useRef<Size>(loadSavedState().size);

  const currentLayout = isNumericInput ? layouts.numpad : layouts[currentLanguage];

  useEffect(() => {
    if (isKeyboardVisible) {
      const saved = loadSavedState();
      const constrained = constrainToViewport(saved.position, saved.size);
      setPosition(constrained.position);
      setSize(constrained.size);
      positionReference.current = constrained.position;
      sizeReference.current = constrained.size;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isKeyboardVisible]);

  useEffect(() => {
    positionReference.current = position;
  }, [position]);

  useEffect(() => {
    sizeReference.current = size;
  }, [size]);

  useEffect(() => {
    if (!isKeyboardVisible) return;

    const handleWindowResize = () => {
      const constrained = constrainToViewport(positionReference.current, sizeReference.current);
      setPosition(constrained.position);
      setSize(constrained.size);
      positionReference.current = constrained.position;
      sizeReference.current = constrained.size;
      localStorage.setItem('virtual-keyboard-state', JSON.stringify(constrained));
    };

    window.addEventListener('resize', handleWindowResize);
    // eslint-disable-next-line consistent-return
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [isKeyboardVisible]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (event_: MouseEvent) => {
      if (!dragStartReference.current) return;

      const deltaX = event_.clientX - dragStartReference.current.x;
      const deltaY = event_.clientY - dragStartReference.current.y;

      const newX = positionReference.current.x + deltaX;
      const newY = positionReference.current.y + deltaY;

      const constrained = constrainToViewport({ x: newX, y: newY }, sizeReference.current);

      setPosition(constrained.position);
      setSize(constrained.size);
      positionReference.current = constrained.position;
      sizeReference.current = constrained.size;

      localStorage.setItem('virtual-keyboard-state', JSON.stringify(constrained));

      dragStartReference.current = { x: event_.clientX, y: event_.clientY };
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      dragStartReference.current = null;
      setTimeout(() => {
        unlockKeyboard();
        if (activeInput) {
          activeInput.focus();
        }
      }, 100);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  useEffect(() => {
    if (!isResizing || !resizeStartReference.current) return;

    const handleMouseMove = (event_: MouseEvent) => {
      if (!resizeStartReference.current || !containerReference.current) return;

      event_.preventDefault();

      const deltaX = event_.clientX - resizeStartReference.current.x;
      const deltaY = event_.clientY - resizeStartReference.current.y;
      const { width: startWidth, height: startHeight } = resizeStartReference.current;

      const rect = containerReference.current.getBoundingClientRect();
      const currentX = rect.left;
      const currentY = rect.top;

      const baseAspectRatio = 1200 / 280;

      const minWidth = 400;
      const scaleFromWidth = (startWidth + deltaX) / startWidth;
      const scaleFromHeight = (startHeight - deltaY) / startHeight;

      const scale = Math.max(scaleFromWidth, scaleFromHeight);

      let newWidth = startWidth * scale;
      let newHeight = newWidth / baseAspectRatio;

      if (newWidth < minWidth) {
        newWidth = minWidth;
        newHeight = minWidth / baseAspectRatio;
      }

      const heightDelta = newHeight - startHeight;
      const newX = currentX;
      const newY = currentY - heightDelta;

      const constrained = constrainToViewport(
        { x: newX, y: newY },
        { width: newWidth, height: newHeight },
      );

      setPosition(constrained.position);
      setSize(constrained.size);
      positionReference.current = constrained.position;
      sizeReference.current = constrained.size;

      localStorage.setItem('virtual-keyboard-state', JSON.stringify(constrained));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setResizeDirection('');
      resizeStartReference.current = null;
      setTimeout(() => {
        unlockKeyboard();
        if (activeInput) {
          activeInput.focus();
        }
      }, 100);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResizing, resizeDirection]);

  useEffect(() => {
    if (activeInput && keyboardReference.current) {
      keyboardReference.current.setInput(activeInput.value);
    }
  }, [activeInput]);

  useEffect(() => {
    if (keyboardReference.current && !isNumericInput) {
      const newLayout = layouts[currentLanguage];
      if (newLayout) {
        keyboardReference.current.setOptions({
          layout: newLayout,
        });
        setLayoutName('default');
      }
    }
  }, [currentLanguage, isNumericInput]);

  useEffect(() => {
    if (showLanguageSelector && languageButtonReference.current) {
      const buttonRect = languageButtonReference.current.getBoundingClientRect();
      const spaceAbove = buttonRect.top;
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const dropdownHeight = 300;

      if (spaceAbove >= dropdownHeight || spaceAbove > spaceBelow) {
        setDropdownPosition('above');
      } else {
        setDropdownPosition('below');
      }
    }
  }, [showLanguageSelector]);

  useEffect(() => {
    if (!showLanguageSelector) return;

    const handleClickOutside = (event_: MouseEvent) => {
      const target = event_.target as HTMLElement;
      if (
        languageButtonReference.current &&
        languageDropdownReference.current &&
        !languageButtonReference.current.contains(target) &&
        !languageDropdownReference.current.contains(target)
      ) {
        setShowLanguageSelector(false);
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLanguageSelector]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (!showLanguageSelector) {
      const timer = setTimeout(() => {
        unlockKeyboard();
        if (activeInput) {
          activeInput.focus();
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [showLanguageSelector, unlockKeyboard, activeInput]);

  const insertTextAtCursor = (text: string) => {
    if (!activeInput) return;

    const maxLength = getMaxLength(activeInput);
    if (maxLength !== undefined && activeInput.value.length >= maxLength) return;

    const start = activeInput.selectionStart || 0;
    const end = activeInput.selectionEnd || 0;
    const currentValue = activeInput.value;

    let newValue =
      currentValue.slice(0, Math.max(0, start)) + text + currentValue.slice(Math.max(0, end));

    if (maxLength !== undefined && newValue.length > maxLength) {
      newValue = newValue.slice(0, maxLength);
    }

    // eslint-disable-next-line @typescript-eslint/unbound-method
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      activeInput.tagName === 'TEXTAREA'
        ? window.HTMLTextAreaElement.prototype
        : window.HTMLInputElement.prototype,
      'value',
    )?.set as ((value: string) => void) | undefined;

    if (nativeInputValueSetter) {
      nativeInputValueSetter.call(activeInput, newValue);
    } else {
      activeInput.value = newValue;
    }

    const inputEvent = new Event('input', { bubbles: true });
    activeInput.dispatchEvent(inputEvent);

    const newCursorPos = Math.min(start + text.length, newValue.length);
    activeInput.setSelectionRange(newCursorPos, newCursorPos);

    if (keyboardReference.current) {
      keyboardReference.current.setInput(newValue);
    }

    activeInput.focus();
  };

  const handleBackspace = () => {
    if (!activeInput) return;

    const start = activeInput.selectionStart || 0;
    const end = activeInput.selectionEnd || 0;
    const currentValue = activeInput.value;

    let newValue: string;
    let newCursorPos: number;

    if (start !== end) {
      newValue = currentValue.slice(0, Math.max(0, start)) + currentValue.slice(Math.max(0, end));
      newCursorPos = start;
    } else if (start > 0) {
      newValue =
        currentValue.slice(0, Math.max(0, start - 1)) + currentValue.slice(Math.max(0, end));
      newCursorPos = start - 1;
    } else {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/unbound-method
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      activeInput.tagName === 'TEXTAREA'
        ? window.HTMLTextAreaElement.prototype
        : window.HTMLInputElement.prototype,
      'value',
    )?.set as ((value: string) => void) | undefined;

    if (nativeInputValueSetter) {
      nativeInputValueSetter.call(activeInput, newValue);
    } else {
      activeInput.value = newValue;
    }

    const inputEvent = new Event('input', { bubbles: true });
    activeInput.dispatchEvent(inputEvent);

    activeInput.setSelectionRange(newCursorPos, newCursorPos);

    if (keyboardReference.current) {
      keyboardReference.current.setInput(newValue);
    }

    activeInput.focus();
  };

  const onKeyPress = (button: string) => {
    if (!activeInput) return;

    if (button === '{shift}' || button === '{lock}') {
      setLayoutName(layoutName === 'default' ? 'shift' : 'default');
      return;
    }

    if (button === '{tab}') {
      const event = new KeyboardEvent('keydown', { key: 'Tab', bubbles: true });
      activeInput.dispatchEvent(event);
      return;
    }

    if (button === '{enter}') {
      if (activeInput.tagName === 'INPUT') {
        const form = activeInput.closest('form');
        if (form) {
          const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
          form.dispatchEvent(submitEvent);
        }
        hideKeyboard();
      } else {
        insertTextAtCursor('\n');
      }
      return;
    }

    if (button === '{bksp}') {
      handleBackspace();
      return;
    }

    if (button === '{space}') {
      insertTextAtCursor(' ');
      return;
    }

    insertTextAtCursor(button);

    if (layoutName === 'shift') {
      setLayoutName('default');
    }
  };

  const handleLanguageChange = (lang: string) => {
    setCurrentLanguage(lang);
    setShowLanguageSelector(false);
    setLayoutName('default');
  };

  const handleDragStart = (event_: React.MouseEvent) => {
    event_.stopPropagation();
    event_.preventDefault();
    if (isResizing) {
      return;
    }

    const target = event_.target as HTMLElement;
    const isButton = target.closest('button');
    const isDropdown = target.closest('.language-dropdown');
    const isKeyboardButton = target.closest('.hg-button');
    const isResizeHandle = target.closest('.resize-handle');
    const isResizeIcon = target.closest('.resize-icon-button');

    if (isButton || isDropdown || isKeyboardButton || isResizeHandle || isResizeIcon) {
      return;
    }

    if (
      target === event_.currentTarget ||
      target.closest('.virtual-keyboard-header') === event_.currentTarget
    ) {
      event_.stopPropagation();
      lockKeyboard();
      setIsDragging(true);
      dragStartReference.current = { x: event_.clientX, y: event_.clientY };
    }
  };

  const handleResizeStart = (event_: React.MouseEvent, direction: string) => {
    event_.stopPropagation();
    event_.preventDefault();
    lockKeyboard();
    setIsResizing(true);
    setResizeDirection(direction);
    if (containerReference.current) {
      const rect = containerReference.current.getBoundingClientRect();
      resizeStartReference.current = {
        x: event_.clientX,
        y: event_.clientY,
        width: rect.width,
        height: rect.height,
      };
    }
  };

  if (!isKeyboardVisible) return null;

  const baseWidth = 1200;
  const baseHeight = 280;
  const scaleX = size.width / baseWidth;
  const scaleY = size.height / baseHeight;
  const scale = Math.max(0.5, Math.min(Math.min(scaleX, scaleY), 1.5));

  const onMouseDown = (event_: React.MouseEvent) => {
    event_.stopPropagation();
    event_.preventDefault();
  };

  const onLanguageButtonClick = (event_: React.MouseEvent) => {
    event_.stopPropagation();
    event_.preventDefault();
    const newState = !showLanguageSelector;
    setShowLanguageSelector(newState);
    if (newState) {
      lockKeyboard();
    }
  };

  const onResize = (event_: React.MouseEvent) => {
    event_.stopPropagation();
    event_.preventDefault();
    handleResizeStart(event_, 'top-right');
  };

  const onHideKeyboard = (event_: React.MouseEvent) => {
    event_.stopPropagation();
    event_.preventDefault();
    hideKeyboard();
  };

  const onLanguageOptionClick = (event_: React.MouseEvent, key: string) => {
    event_.stopPropagation();
    event_.preventDefault();
    handleLanguageChange(key);
  };

  const onContainerMouseDown = (event_: React.MouseEvent) => {
    const target = event_.target as HTMLElement;
    if (
      !target.closest('.virtual-keyboard-header') &&
      !target.closest('.resize-handle') &&
      !target.closest('button') &&
      !target.closest('.language-dropdown') &&
      !target.closest('.resize-icon-button')
    ) {
      event_.preventDefault();
    }
  };

  const onContainerClick = (event_: React.MouseEvent) => {
    const target = event_.target as HTMLElement;
    const isLanguageButton = target.closest('.language-dropdown button');
    if (isLanguageButton) {
      return;
    }
    if (target.closest('.language-dropdown') || target.closest('button')) {
      event_.stopPropagation();
    }
  };

  const onHeaderMouseDown = (event_: React.MouseEvent) => {
    if (!isResizing) {
      handleDragStart(event_);
    }
  };

  return (
    <div
      id="virtual-keyboard-container"
      ref={containerReference}
      className="virtual-keyboard-overlay"
      role="presentation"
      style={
        {
          left: `${position.x}px`,
          top: `${position.y}px`,
          width: `${size.width}px`,
          right: 'auto',
          bottom: 'auto',
          '--keyboard-scale': String(scale),
          pointerEvents: isResizing ? 'none' : 'auto',
        } as React.CSSProperties
      }
      onMouseDown={onContainerMouseDown}
      onClick={onContainerClick}
    >
      <div className="virtual-keyboard-wrapper">
        <div
          className="virtual-keyboard-header"
          role="presentation"
          onMouseDown={onHeaderMouseDown}
          // eslint-disable-next-line unicorn/no-nested-ternary
          style={{ cursor: isDragging ? 'grabbing' : isResizing ? 'nwse-resize' : 'grab' }}
        >
          {!isNumericInput && (
            <div className="language-selector-container">
              <button
                ref={languageButtonReference}
                type="button"
                className="language-button"
                onMouseDown={onMouseDown}
                onClick={onLanguageButtonClick}
              >
                {languageNames[currentLanguage]} ▼
              </button>

              {showLanguageSelector && (
                <div
                  ref={languageDropdownReference}
                  className={`language-dropdown ${
                    dropdownPosition === 'below' ? 'dropdown-below' : ''
                  }`}
                >
                  {Object.entries(languageNames).map(([key, name]) => (
                    <button
                      key={key}
                      type="button"
                      className={`language-option ${currentLanguage === key ? 'active' : ''}`}
                      onMouseDown={onMouseDown}
                      onClick={(event_) => onLanguageOptionClick(event_, key)}
                    >
                      {name} - {key.charAt(0).toUpperCase() + key.slice(1)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="header-actions">
            <button
              type="button"
              className="close-button"
              onMouseDown={onMouseDown}
              onClick={onHideKeyboard}
            >
              ✕
            </button>
            <button
              type="button"
              className="resize-icon-button"
              onMouseDown={onResize}
              onClick={onResize}
              aria-label="Resize keyboard"
            >
              <FaExpandAlt />
            </button>
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          <Keyboard
            key={`${currentLanguage}-${isNumericInput}`}
            keyboardRef={(r) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              keyboardReference.current = r;
            }}
            layout={currentLayout}
            layoutName={layoutName}
            onKeyPress={onKeyPress}
            theme="hg-theme-default virtual-keyboard-theme"
            display={{
              '{bksp}': '⌫',
              '{enter}': '↵',
              '{shift}': '⇧',
              '{lock}': '⇪',
              '{tab}': '⇥',
              '{space}': ' ',
            }}
            buttonTheme={[
              {
                class: 'special-key',
                buttons: '{bksp} {enter} {shift} {lock} {tab}',
              },
              {
                class: 'space-key',
                buttons: '{space}',
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
}

export default VirtualKeyboard;
